import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle2, AlertTriangle, PlayCircle, MonitorSmartphone } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const ICON = { started: PlayCircle, succeeded: CheckCircle2, failed: AlertTriangle };
const COLOR = { started: "text-zinc-400", succeeded: "text-[#7AF25F]", failed: "text-red-400" };
const ago = (d) => { const s = (Date.now() - new Date(d).getTime()) / 1000; return s < 60 ? "just now" : s < 3600 ? `${Math.floor(s / 60)}m ago` : s < 86400 ? `${Math.floor(s / 3600)}h ago` : `${Math.floor(s / 86400)}d ago`; };

export default function NotificationBell() {
  const nav = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const seen = useRef(null);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => {
      const list = r.data.notifications;
      if (seen.current && perm === "granted") {
        list.filter((n) => !n.read && !seen.current.has(n.id)).forEach((n) => {
          try { new Notification(`Ai Xtudio · ${n.title}`, { body: n.body, icon: "/logo.png", tag: n.id }); } catch { /* ignore */ }
        });
      }
      seen.current = new Set(list.map((n) => n.id));
      setData(r.data);
    }).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [perm]);

  const enableDesktop = async () => {
    if (typeof Notification === "undefined") return;
    setPerm(await Notification.requestPermission());
  };

  return (
    <Popover onOpenChange={(o) => { if (o && data.unread) api.post("/notifications/read").then(() => setData((d) => ({ ...d, unread: 0 }))); }}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-lg border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-700" data-testid="nav-notifications">
          <Bell className="w-4 h-4" />
          {data.unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full ax-gradient-bg text-black text-[10px] font-bold flex items-center justify-center" data-testid="nav-notifications-count">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-[#121214] border-zinc-800 text-zinc-100 rounded-xl" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <span className="font-heading font-semibold">Notifications</span>
          {perm !== "granted" && perm !== "unsupported" && (
            <button onClick={enableDesktop} className="text-[11px] text-[#7AF25F] hover:underline flex items-center gap-1" data-testid="notifications-enable-desktop">
              <MonitorSmartphone className="w-3.5 h-3.5" />Enable desktop alerts
            </button>
          )}
          {perm === "granted" && <span className="text-[10px] font-mono text-zinc-500">Desktop alerts on</span>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.notifications.length === 0 && <p className="text-sm text-zinc-500 text-center py-10">No notifications yet.</p>}
          {data.notifications.map((n) => {
            const I = ICON[n.kind] || Bell;
            return (
              <button key={n.id} onClick={() => n.project_id && nav(`/studio/${n.project_id}/${n.section || "image"}`)} data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-zinc-800/50 border-b border-zinc-800/60 ${n.read ? "" : "bg-[#7AF25F]/[0.04]"}`}>
                <I className={`w-4 h-4 mt-0.5 shrink-0 ${COLOR[n.kind] || ""}`} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-white">{n.title}</span>
                  <span className="block text-xs text-zinc-500 line-clamp-2">{n.body}</span>
                </span>
                <span className="text-[10px] font-mono text-zinc-600 whitespace-nowrap">{ago(n.created_at)}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
